import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useChatStore } from '../store/chat';
import { useSettingsStore } from '../store/settings';
import { useWorkflowStore } from '../store/workflow';
import { useUIStore } from '../store/ui';
import { useCreditsStore } from '../store/credits';
import { useCredentialsStore } from '../store/credentials';
import { useCredits } from '../hooks/useCredits';
import { createApiClient } from '@n8n-builder/api-client';
import type { Workflow } from '@n8n-builder/n8n-types';
import { ChatHeader, ChatInput, ChatMessages } from '../components/chat';
import {
  CheckIcon,
  XMarkIcon,
} from '../components/ui/icons';
import { API_BASE_URL } from '../lib/api-config';
import { extractCredentialRequirements } from '../utils/credentials';
import { TIER_INFO, TIER_CREDITS, type SubscriptionTier } from '@n8n-builder/shared';

interface DeployResult {
  type: 'success' | 'error';
  message: string;
  workflowUrl?: string;
}

export function ChatPage() {
  const { conversationId: urlConversationId } = useParams<{ conversationId?: string }>();
  const navigate = useNavigate();
  const [input, setInput] = useState('');
  const [deployingWorkflowId, setDeployingWorkflowId] = useState<string | null>(null);
  const [deployResult, setDeployResult] = useState<DeployResult | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [isBuilding, setIsBuilding] = useState(false);
  const [isAnswering, setIsAnswering] = useState(false);
  const [showCredentialCard, setShowCredentialCard] = useState(false);

  const {
    messages,
    addMessage,
    isLoading,
    setLoading,
    currentWorkflow,
    currentWorkflowId,
    currentPlan,
    currentQuestions,
    currentPhase,
    setCurrentPlan,
    setCurrentPhase,
    setCurrentQuestions,
    clearQuestions,
    conversationId,
    setConversationId,
    addConversation,
    updateConversationInList,
    streamingState,
    setThinking,
    addToolCall,
    completeToolCall,
    clearStreamingState,
    startNewConversation,
    addDebugLog,
    getDebugLogs,
    loadingConversationId,
    setLoadingConversationId,
    loadConversationFast,
    attachedWorkflow,
    setAttachedWorkflow,
    clearAttachedWorkflow,
  } = useChatStore();

  const {
    hasValidCredentials,
    chatMode,
    setChatMode,
    selectedModel,
    setSelectedModel,
    openUpgradeModal,
    fetchActiveInstance,
  } = useSettingsStore();
  const { sidePanelOpen, resetSections, openSidePanel, setActiveSidePanelTab } = useUIStore();
  const decrementCredits = useCreditsStore((state) => state.decrementCredits);
  const { refetch: refetchCredits } = useCredits();

  // Checkout success handling
  const [searchParams, setSearchParams] = useSearchParams();
  const [showCheckoutSuccess, setShowCheckoutSuccess] = useState(false);
  const [checkoutPlan, setCheckoutPlan] = useState<SubscriptionTier | null>(null);
  const confettiCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (searchParams.get('checkout') === 'success') {
      const plan = (searchParams.get('plan') as SubscriptionTier) || 'pro';
      setCheckoutPlan(plan);

      // Clean up the URL immediately
      searchParams.delete('checkout');
      searchParams.delete('plan');
      setSearchParams(searchParams, { replace: true });

      // Show the modal immediately with the plan from the URL
      setShowCheckoutSuccess(true);

      // Poll for the webhook to update the DB (every 2s, up to 5 attempts)
      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        await refetchCredits();
        const current = useCreditsStore.getState().subscription;
        if (current === plan || attempts >= 5) {
          clearInterval(poll);
        }
      }, 2000);

      return () => clearInterval(poll);
    }
  }, []);

  // Confetti animation
  useEffect(() => {
    if (!showCheckoutSuccess || !confettiCanvasRef.current) return;
    const canvas = confettiCanvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const colors = ['#7C3AED', '#2563EB', '#10B981', '#F59E0B', '#EF4444', '#EC4899', '#8B5CF6'];
    const particles: Array<{
      x: number; y: number; w: number; h: number;
      color: string; vx: number; vy: number;
      rotation: number; rotationSpeed: number; opacity: number;
    }> = [];

    for (let i = 0; i < 150; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: -20 - Math.random() * canvas.height * 0.5,
        w: 4 + Math.random() * 6,
        h: 8 + Math.random() * 8,
        color: colors[Math.floor(Math.random() * colors.length)] ?? '#7C3AED',
        vx: (Math.random() - 0.5) * 4,
        vy: 2 + Math.random() * 4,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.2,
        opacity: 1,
      });
    }

    let frame: number;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let alive = false;
      for (const p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += 0.05;
        p.rotation += p.rotationSpeed;
        p.vx *= 0.99;
        if (p.y > canvas.height * 0.7) p.opacity -= 0.02;
        if (p.opacity <= 0) continue;
        alive = true;
        ctx.save();
        ctx.globalAlpha = Math.max(0, p.opacity);
        ctx.translate(p.x, p.y);
        ctx.rotate(p.rotation);
        ctx.fillStyle = p.color;
        ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      }
      if (alive) frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, [showCheckoutSuccess]);

  // Load active instance on mount (cache-aware)
  useEffect(() => {
    fetchActiveInstance();
  }, []);

  // Load conversation from URL on mount/refresh
  useEffect(() => {
    if (urlConversationId && urlConversationId !== conversationId && messages.length === 0) {
      const loadFromUrl = async () => {
        setLoadingConversationId(urlConversationId);
        try {
          const apiClient = getApiClient();
          const result = await apiClient.getConversationFull(urlConversationId);
          if (result.data) {
            loadConversationFast(result.data);
          } else {
            setLoadingConversationId(null);
            navigate('/workflow-builder', { replace: true });
          }
        } catch {
          setLoadingConversationId(null);
          navigate('/workflow-builder', { replace: true });
        }
      };
      loadFromUrl();
    }
  }, [urlConversationId]);

  // Keep URL in sync with conversation state
  useEffect(() => {
    if (conversationId && !urlConversationId) {
      navigate(`/workflow-builder/${conversationId}`, { replace: true });
    } else if (!conversationId && urlConversationId) {
      navigate('/workflow-builder', { replace: true });
    }
  }, [conversationId]);

  // Auto-dismiss deploy result after 5 seconds
  useEffect(() => {
    if (deployResult) {
      const timer = setTimeout(() => setDeployResult(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [deployResult]);

  const getApiClient = () => {
    return createApiClient(API_BASE_URL);
  };

  const triggerCredentialCheck = async (workflow: Workflow) => {
    const { activeInstance } = useSettingsStore.getState();
    if (!activeInstance) return;

    const reqs = extractCredentialRequirements(workflow);
    if (reqs.length === 0) return;

    const store = useCredentialsStore.getState();

    try {
      store.setPanelState('loading');
      const apiClient = getApiClient();
      const result = await apiClient.listN8nCredentials();
      if (result.error || !result.data?.credentials) {
        store.setPanelState('error', result.error || 'Failed to fetch credentials');
        store.setRequirements(reqs);
        return;
      }

      const available = result.data.credentials;
      store.setAvailableCredentials(available);
      store.setPanelState('ready');

      // Count any matches (but don't auto-map)
      let hasAnyMatch = false;
      for (const req of reqs) {
        const matching = available.filter((c) => c.type === req.credentialType);
        if (matching.length > 0) hasAnyMatch = true;
      }

      store.setRequirements(reqs);

      // Show the inline card if any matching credentials exist
      if (hasAnyMatch) {
        setShowCredentialCard(true);
      }
    } catch {
      store.setPanelState('error', 'Failed to connect to n8n instance');
      store.setRequirements(reqs);
    }
  };

  const handleDismissCredentialCard = () => {
    setShowCredentialCard(false);
  };

  // Load debug logs from server when conversationId changes (handles page refresh, re-navigation)
  useEffect(() => {
    if (!conversationId) return;

    const existingLogs = getDebugLogs(conversationId);
    if (existingLogs.length > 0) return;

    const loadDebugLogs = async () => {
      try {
        const apiClient = getApiClient();
        const result = await apiClient.getConversationDebugLogs(conversationId);
        if (result.data?.debugLogs) {
          for (const log of result.data.debugLogs) {
            addDebugLog(conversationId, log);
          }
        }
      } catch {
        // Non-critical, ignore errors
      }
    };

    loadDebugLogs();
  }, [conversationId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    let userMessage = input.trim();

    // Append attachment note if present
    if (attachedWorkflow) {
      userMessage += `\n\n[Attached workflow: ${attachedWorkflow.workflow.name} (${attachedWorkflow.nodeCount} nodes)]`;
    }

    setInput('');

    // Add user message to chat
    addMessage({ role: 'user', content: userMessage });

    setLoading(true);
    setStatusMessage(null);
    clearStreamingState();
    resetSections();
    setCurrentPhase('planning');
    setShowCredentialCard(false);

    try {
      const apiClient = getApiClient();

      // Capture attachment before clearing (will be cleared in onComplete)
      const workflowToAttach = attachedWorkflow?.workflow;

      // Use streaming endpoint for real-time status updates
      apiClient.sendMessageStream(
        userMessage,
        {
          onStatus: (message) => {
            setStatusMessage(message);
          },
          onThinking: (content) => {
            setThinking(content);
            setStatusMessage('Thinking...');
          },
          onToolStart: (tool, input, iteration) => {
            addToolCall(tool, input, iteration);
          },
          onToolEnd: (tool, result, iteration) => {
            completeToolCall(tool, iteration, result);
          },
          onPlanCreated: (plan) => {
            clearStreamingState();
            setLoading(false);
            setStatusMessage(null);
            setCurrentPlan(plan);
            setCurrentPhase('reviewing');
            // Open side panel and switch to plan tab
            openSidePanel();
            setActiveSidePanelTab('plan');
            // Optimistically decrement credits (plan creation costs 1 credit)
            decrementCredits(1);
          },
          onQuestionsAsked: (questions) => {
            setCurrentQuestions(questions);
            setCurrentPhase('answering');
          },
          onComplete: (data) => {
            setStatusMessage(null);
            setLoading(false);
            clearStreamingState();

            // Update conversationId if this was a new conversation
            if (!conversationId && data.conversationId) {
              setConversationId(data.conversationId);

              // Add to conversation list with AI-generated title if available
              addConversation({
                id: data.conversationId,
                title: data.conversationTitle || userMessage.slice(0, 100),
                createdAt: Math.floor(Date.now() / 1000),
                updatedAt: Math.floor(Date.now() / 1000),
              });
            } else if (conversationId && data.conversationTitle) {
              // Update existing conversation title if a new one was provided
              updateConversationInList(conversationId, { title: data.conversationTitle });
            }

            // Store debug log if present (dev mode only)
            if (data.debugLog && data.conversationId) {
              console.log('[Debug] Received debug log with', data.debugLog.entries?.length, 'entries');
              addDebugLog(data.conversationId, data.debugLog);
            } else {
              console.log('[Debug] No debug log in response. debugLog:', !!data.debugLog, 'conversationId:', data.conversationId);
            }

            addMessage({
              role: 'assistant',
              content: data.response,
              workflow: data.workflow,
              workflowId: data.workflowId,
              plan: data.plan,
            });

            // If a workflow was generated (during build), switch to workflow tab and edit mode
            if (data.workflow) {
              setCurrentPhase('idle');
              setActiveSidePanelTab('workflow');
              setChatMode('edit');
              // FIX: Only clear attachment when a new workflow is generated.
              // This preserves the attached workflow context for follow-up messages
              // when Claude asks clarifying questions before generating.
              clearAttachedWorkflow();
              // Cross-store: invalidate workflows cache so Workflows page shows new data
              useWorkflowStore.getState().invalidateWorkflows();
            }
          },
          onError: (error) => {
            setStatusMessage(null);
            setLoading(false);
            setCurrentPhase('idle');
            clearStreamingState();
            addMessage({
              role: 'assistant',
              content: `Sorry, I encountered an error: ${error}`,
            });
          },
          onInsufficientCredits: (remainingCredits) => {
            setStatusMessage(null);
            setLoading(false);
            setCurrentPhase('idle');
            clearStreamingState();
            refetchCredits();
            addMessage({
              role: 'assistant',
              content: `You don't have enough credits. You have ${remainingCredits} credits remaining. [Upgrade your plan](#upgrade) to continue.`,
            });
            openUpgradeModal();
          },
        },
        conversationId || undefined,
        { mode: chatMode, model: selectedModel, attachedWorkflow: workflowToAttach }
      );
    } catch {
      setStatusMessage(null);
      setLoading(false);
      setCurrentPhase('idle');
      clearStreamingState();
      addMessage({
        role: 'assistant',
        content: 'Sorry, something went wrong. Please try again.',
      });
    }
  };

  const handleBuild = async () => {
    if (!currentPlan || !conversationId || isBuilding) return;

    // Add user message to chat (matches what API saves to DB)
    addMessage({ role: 'user', content: 'Begin building' });

    setIsBuilding(true);
    setCurrentPhase('building');
    setStatusMessage('Building workflow...');
    setShowCredentialCard(false);

    try {
      const apiClient = getApiClient();

      apiClient.buildFromPlan(
        conversationId,
        currentPlan,
        {
          onStatus: (message) => {
            console.log('Build onStatus:', message);
            setStatusMessage(message);
          },
          onBuildStarted: () => {
            setStatusMessage('Building workflow...');
            // Optimistically decrement credits (build from plan costs 1 credit)
            decrementCredits(1);
          },
          onBuildProgress: (step) => {
            setStatusMessage(step);
          },
          onComplete: (data) => {
            setStatusMessage(null);
            setIsBuilding(false);
            setCurrentPhase('idle');

            // Store debug log if present (dev mode only)
            if (data.debugLog && conversationId) {
              addDebugLog(conversationId, data.debugLog);
            } else if (!data.debugLog && conversationId) {
              // Fallback: fetch debug logs from server if not returned inline
              const fallbackApiClient = getApiClient();
              fallbackApiClient.getConversationDebugLogs(conversationId).then((result) => {
                if (result.data?.debugLogs) {
                  // Only add logs we don't already have
                  const existing = getDebugLogs(conversationId);
                  const existingIds = new Set(existing.map((l) => l.messageId));
                  for (const log of result.data.debugLogs) {
                    if (!existingIds.has(log.messageId)) {
                      addDebugLog(conversationId, log);
                    }
                  }
                }
              }).catch(() => { /* non-critical */ });
            }

            addMessage({
              role: 'assistant',
              content: data.response,
              workflow: data.workflow,
              workflowId: data.workflowId,
            });

            // Switch to workflow tab and edit mode if workflow was generated
            if (data.workflow) {
              setActiveSidePanelTab('workflow');
              setChatMode('edit');
              // Cross-store: invalidate workflows cache so Workflows page shows new data
              useWorkflowStore.getState().invalidateWorkflows();
              // Trigger inline credential card
              triggerCredentialCheck(data.workflow);
            }
          },
          onError: (error) => {
            setStatusMessage(null);
            setIsBuilding(false);
            setCurrentPhase('reviewing');
            addMessage({
              role: 'assistant',
              content: `Failed to build workflow: ${error}`,
            });
          },
          onInsufficientCredits: (remainingCredits) => {
            setStatusMessage(null);
            setIsBuilding(false);
            setCurrentPhase('reviewing');
            refetchCredits();
            addMessage({
              role: 'assistant',
              content: `You don't have enough credits. You have ${remainingCredits} credits remaining. [Upgrade your plan](#upgrade) to continue.`,
            });
            openUpgradeModal();
          },
        },
        { model: selectedModel }
      );
    } catch {
      setStatusMessage(null);
      setIsBuilding(false);
      setCurrentPhase('reviewing');
      addMessage({
        role: 'assistant',
        content: 'Sorry, something went wrong while building. Please try again.',
      });
    }
  };

  const handleAnswerQuestions = async (answers: Record<string, string>) => {
    if (!currentQuestions || !conversationId || isAnswering) return;

    setIsAnswering(true);

    // Format all answers as a structured user message
    const answerLines = currentQuestions.questions.map((q) => {
      const answer = answers[q.id] || '(not answered)';
      return `· ${q.question} → ${answer}`;
    });
    const answerText = `**Answers to Questions**\n${answerLines.join('\n')}`;

    // Add user's answers as a message
    addMessage({ role: 'user', content: answerText });

    // Clear the questions state
    clearQuestions();
    setCurrentPhase('planning');
    clearStreamingState();

    try {
      const apiClient = getApiClient();

      // Send the answers as a continuation of the conversation
      apiClient.sendMessageStream(
        answerText,
        {
          onStatus: (message) => {
            setStatusMessage(message);
          },
          onThinking: (content) => {
            setThinking(content);
            setStatusMessage('Thinking...');
          },
          onToolStart: (tool, input, iteration) => {
            addToolCall(tool, input, iteration);
          },
          onToolEnd: (tool, result, iteration) => {
            completeToolCall(tool, iteration, result);
          },
          onPlanCreated: (plan) => {
            clearStreamingState();
            setLoading(false);
            setStatusMessage(null);
            setCurrentPlan(plan);
            setCurrentPhase('reviewing');
            openSidePanel();
            setActiveSidePanelTab('plan');
            decrementCredits(1);
          },
          onQuestionsAsked: (questions) => {
            setCurrentQuestions(questions);
            setCurrentPhase('answering');
          },
          onComplete: (data) => {
            setStatusMessage(null);
            setIsAnswering(false);
            clearStreamingState();

            // Store debug log if present (dev mode only)
            if (data.debugLog && conversationId) {
              addDebugLog(conversationId, data.debugLog);
            }

            addMessage({
              role: 'assistant',
              content: data.response,
              workflow: data.workflow,
              workflowId: data.workflowId,
              plan: data.plan,
            });

            if (data.workflow) {
              setCurrentPhase('idle');
              setActiveSidePanelTab('workflow');
            }
          },
          onError: (error) => {
            setStatusMessage(null);
            setIsAnswering(false);
            setCurrentPhase('idle');
            clearStreamingState();
            addMessage({
              role: 'assistant',
              content: `Sorry, I encountered an error: ${error}`,
            });
          },
          onInsufficientCredits: (remainingCredits) => {
            setStatusMessage(null);
            setIsAnswering(false);
            setCurrentPhase('idle');
            clearStreamingState();
            refetchCredits();
            addMessage({
              role: 'assistant',
              content: `You don't have enough credits. You have ${remainingCredits} credits remaining. [Upgrade your plan](#upgrade) to continue.`,
            });
            openUpgradeModal();
          },
        },
        conversationId,
        { mode: chatMode, model: selectedModel }
      );
    } catch {
      setStatusMessage(null);
      setIsAnswering(false);
      setCurrentPhase('idle');
      clearStreamingState();
      addMessage({
        role: 'assistant',
        content: 'Sorry, something went wrong. Please try again.',
      });
    }
  };

  const handleDeploy = useCallback(async (workflow: Workflow, workflowId?: string) => {
    // Clear any previous result
    setDeployResult(null);

    // Check for valid credentials
    if (!hasValidCredentials()) {
      setDeployResult({
        type: 'error',
        message: 'No n8n instance configured. Go to Settings or MCP to configure one.',
      });
      return;
    }

    const deployId = workflowId || workflow.id;
    setDeployingWorkflowId(deployId);

    try {
      const apiClient = getApiClient();

      const result = await apiClient.deployWorkflow(workflow, workflowId);

      if (result.error) {
        setDeployResult({
          type: 'error',
          message: result.error,
        });
      } else if (result.data?.success) {
        setDeployResult({
          type: 'success',
          message: 'Workflow deployed successfully!',
          workflowUrl: result.data.workflowUrl,
        });
      } else {
        setDeployResult({
          type: 'error',
          message: result.data?.error || 'Deployment failed',
        });
      }
    } catch (error) {
      setDeployResult({
        type: 'error',
        message: error instanceof Error ? error.message : 'Deployment failed',
      });
    } finally {
      setDeployingWorkflowId(null);
    }
  }, [getApiClient, hasValidCredentials]);

  const dismissResult = () => setDeployResult(null);

  // Listen for deploy events from the side panel in Layout
  useEffect(() => {
    const handleDeployEvent = (event: Event) => {
      const customEvent = event as CustomEvent<{ workflow: Workflow; workflowId?: string }>;
      handleDeploy(customEvent.detail.workflow, customEvent.detail.workflowId);
    };
    window.addEventListener('deploy-workflow', handleDeployEvent);
    return () => {
      window.removeEventListener('deploy-workflow', handleDeployEvent);
    };
  }, [handleDeploy]);

  // Listen for build events from the side panel in Layout
  useEffect(() => {
    const handleBuildEvent = () => {
      handleBuild();
    };
    window.addEventListener('build-workflow', handleBuildEvent);
    return () => {
      window.removeEventListener('build-workflow', handleBuildEvent);
    };
  }, [currentPlan, conversationId, isBuilding]);

  // Switch to edit mode when loading a conversation with a workflow
  useEffect(() => {
    if (currentWorkflow && chatMode !== 'edit') {
      setChatMode('edit');
    } else if (!currentWorkflow && !attachedWorkflow && chatMode === 'edit') {
      setChatMode('plan'); // Reset to plan if no workflow
    }
  }, [currentWorkflow, attachedWorkflow, chatMode, setChatMode]);

  return (
    <div className="flex h-full flex-col bg-white">
        {/* Confetti canvas */}
        {showCheckoutSuccess && (
          <canvas
            ref={confettiCanvasRef}
            className="pointer-events-none fixed inset-0 z-[100]"
          />
        )}

        {/* Checkout Success Modal */}
        {showCheckoutSuccess && (
          <div className="fixed inset-0 z-[90] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl animate-fade-in">
              <div className="p-8 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                  <CheckIcon className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Welcome to {TIER_INFO[(checkoutPlan || 'pro') as SubscriptionTier]?.name}!</h2>
                <p className="mt-2 text-sm text-gray-600">
                  Your subscription is active. You now have {TIER_CREDITS[(checkoutPlan || 'pro') as SubscriptionTier]} credits per month.
                </p>

                <div className="mt-6 rounded-lg bg-gray-50 p-4 text-left">
                  <h3 className="text-sm font-semibold text-gray-900 mb-3">Next steps</h3>
                  <ol className="space-y-2 text-sm text-gray-600">
                    <li className="flex items-start gap-2">
                      <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">1</span>
                      <span>Describe a workflow below to get started</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">2</span>
                      <span>Connect your n8n instance in <button onClick={() => { setShowCheckoutSuccess(false); navigate('/connect'); }} className="font-medium text-primary-600 underline hover:text-primary-800">Settings</button></span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-bold text-primary-700">3</span>
                      <span>Deploy your generated workflows with one click</span>
                    </li>
                  </ol>
                </div>
              </div>
              <div className="border-t border-gray-200 px-8 py-4">
                <button
                  onClick={() => setShowCheckoutSuccess(false)}
                  className="w-full rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-primary-700"
                >
                  Start Building
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Header */}
        <ChatHeader />

        {/* Deploy Result Toast */}
        {deployResult && (
          <div className="absolute right-3 top-16 md:right-6 md:top-20 z-50 toast-enter">
            <div
              className={`flex items-center gap-3 rounded-xl border px-4 py-3 shadow-lg ${
                deployResult.type === 'success'
                  ? 'border-green-200 bg-green-50'
                  : 'border-red-200 bg-red-50'
              }`}
            >
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full ${
                  deployResult.type === 'success' ? 'bg-green-100' : 'bg-red-100'
                }`}
              >
                {deployResult.type === 'success' ? (
                  <CheckIcon className="h-4 w-4 text-green-600" />
                ) : (
                  <XMarkIcon className="h-4 w-4 text-red-600" />
                )}
              </div>
              <div className="flex-1">
                <p
                  className={`text-sm font-medium ${
                    deployResult.type === 'success'
                      ? 'text-green-800'
                      : 'text-red-800'
                  }`}
                >
                  {deployResult.message}
                </p>
                {deployResult.workflowUrl && (
                  <a
                    href={deployResult.workflowUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm font-medium text-green-700 underline hover:text-green-900"
                  >
                    Open in n8n
                  </a>
                )}
              </div>
              <button
                onClick={dismissResult}
                className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto px-3 py-4 md:px-6 md:py-6">
          {loadingConversationId && messages.length === 0 ? (
            <div className="mx-auto max-w-3xl space-y-4 animate-pulse">
              <div className="flex gap-3">
                <div className="h-8 w-8 rounded-full bg-gray-200" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-3/4 rounded bg-gray-200" />
                  <div className="h-4 w-1/2 rounded bg-gray-200" />
                </div>
              </div>
              <div className="flex gap-3">
                <div className="h-8 w-8 rounded-full bg-gray-100" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-full rounded bg-gray-100" />
                  <div className="h-4 w-5/6 rounded bg-gray-100" />
                  <div className="h-4 w-2/3 rounded bg-gray-100" />
                </div>
              </div>
            </div>
          ) : (
          <ChatMessages
            messages={messages}
            isLoading={isLoading || isBuilding || isAnswering}
            statusMessage={statusMessage}
            streamingToolCalls={streamingState.toolCalls}
            currentPlan={currentPlan}
            currentQuestions={currentQuestions}
            currentPhase={currentPhase}
            onBuild={handleBuild}
            onAnswerQuestions={handleAnswerQuestions}
            isBuilding={isBuilding}
            isAnswering={isAnswering}
            sidePanelOpen={sidePanelOpen}
            showCredentialCard={showCredentialCard}
            currentWorkflow={currentWorkflow}
            currentWorkflowId={currentWorkflowId || undefined}
            onDismissCredentialCard={handleDismissCredentialCard}
          />
          )}
        </div>

        {/* Input Area */}
        <ChatInput
          value={input}
          onChange={setInput}
          onSubmit={handleSubmit}
          disabled={isLoading || isBuilding}
          isLoading={isLoading || isBuilding}
          mode={chatMode}
          onModeChange={setChatMode}
          model={selectedModel}
          onModelChange={setSelectedModel}
          workflow={currentWorkflow}
          workflowId={currentWorkflowId}
          onDeploy={handleDeploy}
          isDeploying={!!deployingWorkflowId}
          sidePanelOpen={sidePanelOpen}
          onStartNewChat={startNewConversation}
          attachedWorkflow={attachedWorkflow}
          onAttachWorkflow={setAttachedWorkflow}
          onRemoveAttachment={clearAttachedWorkflow}
        />
      </div>
  );
}
